/* eslint-disable max-lines -- This is totally unavoidable in this case. */
import { EvaluatingAppRoles } from 'connect-graphql/auth/roles';

import { AppRole } from '@craft/database';
import { FeatureFlagId } from '@craft/feature-flags';
import { ensureArrayIsNonEmpty } from '@craft/lib';

import { AllowedThoughtspotDashboardRoles } from '@/src/integrations/thoughtspot/permissions';
import { configureUiAwarePages } from '@/src/lib/pages';

import { PageIntegration, PageLayoutVersion } from './types';
import * as util from './util';

/**
 * The application's UI-aware pages configuration, {@link IUiAwarePagesConfiguration}, which serves
 * as the single source of truth for every page that this application renders.
 *
 * Each entry combines the edge-safe {@link PageConfiguration} fields declared on the
 * corresponding {@link EdgeSafePage} in {@link EdgeSafePages} (its `id`, `path`, `matcher`, and
 * `isPublic`) with UI-aware fields from {@link UiAwarePageConfiguration} (its `activeMatcher` and
 * `title`) plus a {@link PageLayoutVersion}, all of which are only relevant inside the React tree
 * or during SSR.
 *
 * The resulting {@link IUiAwarePagesConfiguration} exposes strongly-typed helpers for resolving a
 * page by id or path, constructing concrete paths from path parameters, and determining which
 * page is active for a given pathname.
 *
 * Server-side UI code that runs inside the Next.js page lifecycle (e.g. {@link getServerSideProps}
 * handlers, page components, React components, and hooks) should consume {@link Pages} when it
 * needs UI-aware behavior. Only the edge middleware should consume the narrower
 * {@link EdgeSafePages}, since its UI-unaware fields are the only fields that are safe to depend
 * on at the edge runtime.
 *
 * @see {@link configureUiAwarePages}
 * @see {@link EdgeSafePages}
 */
export const Pages = configureUiAwarePages([
  util.Page('home', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Home',
  } as const),
  util.Page('welcome', {
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    title: null,
  } as const),
  util.Page('dashboard', {
    activeMatcher: null,
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN, AppRole.INSTRUCTOR, AppRole.OBSERVER],
    title: 'Dashboard',
  } as const),
  util.Page('learner-cohorts', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN, AppRole.FACILITATOR, AppRole.INSTRUCTOR],
    title: 'Active Cohorts',
  } as const),
  util.Page('learner-cohort', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN, AppRole.FACILITATOR, AppRole.INSTRUCTOR],
    title: 'Active Cohorts',
  } as const),
  util.Page('archived-learner-cohorts', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Archived Cohorts',
  } as const),
  util.Page('activity', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Activity',
  } as const),
  util.Page('activity-assigned-learners', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Activity',
  } as const),
  util.Page('programs', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: ensureArrayIsNonEmpty(
      Object.values(AppRole).filter(
        (role): role is Exclude<AppRole, typeof AppRole.LEARNER> => role !== AppRole.LEARNER,
      ),
    ),
    title: 'Programs',
  } as const),
  util.Page('archived-programs', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Archived Programs',
  } as const),
  util.Page('sponsored-programs', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Sponsored Programs',
  } as const),
  util.Page('program', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Program',
  } as const),
  util.Page('program-assigned-learners', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Program',
  } as const),
  util.Page('level', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Level',
  } as const),
  util.Page('level-assigned-learners', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Level',
  } as const),
  util.Page('plan', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Plan',
  } as const),
  util.Page('plan-assigned-learners', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Plan',
  } as const),
  util.Page('ai-rubric-import', {
    activeMatcher: null,
    featureFlag: FeatureFlagId.AiRubricImport,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'AI Rubric Import',
  } as const),
  util.Page('rubric', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Rubric',
  } as const),
  util.Page('rubrics', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Rubrics',
  } as const),
  util.Page('manage-users', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'People',
  } as const),
  util.Page('add-user', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Add People',
  } as const),
  util.Page('manage-user', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Manage User',
  } as const),
  util.Page('assigned-program', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [...EvaluatingAppRoles, AppRole.LEARNER],
    title: 'Assigned Program',
  } as const),
  util.Page('assigned-level', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [...EvaluatingAppRoles, AppRole.LEARNER],
    title: 'Assigned Level',
  } as const),
  util.Page('assigned-activity', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [...EvaluatingAppRoles, AppRole.LEARNER],
    title: 'Assigned Activity',
  } as const),
  util.Page('assigned-plan', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [...EvaluatingAppRoles, AppRole.LEARNER],
    title: 'Assigned Plan',
  } as const),
  util.Page('assign-learners', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Assign Learners',
  } as const),
  util.Page('assign-evaluators', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Assign Evaluators',
  } as const),
  util.Page('configure-observations', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Skills Observation Tracking Settings',
  } as const),
  util.Page('coursework', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Coursework',
  } as const),
  util.Page('pending-approvals-activities', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Pending Approvals',
  } as const),
  util.Page('pending', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.EVALUATOR],
    title: 'Pending Approvals',
  } as const),
  util.Page('pending-approvals-not-configured', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Pending Approvals',
  } as const),
  util.Page('pending-approvals-time-entries', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Pending Approvals',
  } as const),
  util.Page('pending-approvals-observations', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Pending Approvals',
  } as const),
  util.Page('reports', {
    activeMatcher: null,
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    roles: [...AllowedThoughtspotDashboardRoles],
    title: 'Reports',
  } as const),
  util.Page('absences-report', {
    activeMatcher: null,
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Absences Report',
  } as const),
  util.Page('thoughtspot-report', {
    activeMatcher: null,
    featureFlag: FeatureFlagId.ThoughtSpotEmbed,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    roles: [...AllowedThoughtspotDashboardRoles],
    title: 'Report',
  } as const),
  util.Page('metabase-report', {
    activeMatcher: null,
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    roles: [...AllowedThoughtspotDashboardRoles],
    title: 'Report',
  } as const),
  util.Page('edlink', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.LEARNER],
    title: 'Edlink Local Testing',
  } as const),
  util.Page('edlink-admin-organization-scope', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Edlink Admin',
  } as const),
  util.Page('program-edlink', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Program Edlink',
  } as const),
  util.Page('not-found', {
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    title: 'Page Not Found',
  } as const),
  util.Page('server-error', {
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    title: 'Server Error',
  } as const),
  util.Page('docs-swagger', {
    integrations: null,
    layoutVersion: PageLayoutVersion.Version2,
    title: 'API Documentation',
  } as const),
  util.Page('lti-select-activity', {
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version1,
    roles: null,
    title: null,
  } as const),
  util.Page('lti-show-activity', {
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version1,
    roles: null,
    title: null,
  } as const),
  util.Page('lti-speed-grader', {
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version1,
    roles: null,
    title: null,
  } as const),
  util.Page('lti-submit-activity', {
    featureFlag: null,
    integrations: null,
    layoutVersion: PageLayoutVersion.Version1,
    roles: null,
    title: null,
  } as const),
  util.Page('settings', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: ensureArrayIsNonEmpty(
      Object.values(AppRole).filter(
        (role): role is Exclude<AppRole, typeof AppRole.LEARNER> => role !== AppRole.LEARNER,
      ),
    ),
    title: 'Settings',
  } as const),
  util.Page('support', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Support',
  } as const),
  util.Page('switch-view', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Switch View',
  } as const),
  util.Page('my-profile', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'My Profile',
  } as const),
  util.Page('craft-admin', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Craft Admin',
  } as const),
  util.Page('unreachable-accounts', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'Unreachable Accounts',
  } as const),
  util.Page('learners', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'My Learners',
  } as const),
  util.Page('learner', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Programs',
  } as const),
  util.Page('manage-user-tags', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'User Tags',
  } as const),
  util.Page('assigned-programs', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.LEARNER],
    title: 'Assigned Programs',
  } as const),
  util.Page('batch-configure', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Batch Configure',
  } as const),
  util.Page('assign-plan', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Assign Plan',
  } as const),
  util.Page('sponsored-organizations', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Sponsored Organizations',
  } as const),
  util.Page('add-sponsored-organization', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Add Sponsored Organization',
  } as const),
  util.Page('system-organizations', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'System Organizations',
  } as const),
  util.Page('add-system-organization', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Add System Organization',
  } as const),
  util.Page('raps', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN, AppRole.LEARNER],
    title: 'RAPs',
  } as const),
  util.Page('rap', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'RAP',
  } as const),
  util.Page('assign-rap', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Assign RAP',
  } as const),
  util.Page('rap-assigned-learners', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'RAP',
  } as const),
  util.Page('rap-competencies', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: [AppRole.ADMIN],
    title: 'RAP',
  } as const),
  util.Page('rap-level', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'RAP Level',
  } as const),
  util.Page('rap-plan', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'RAP Plan',
  } as const),
  util.Page('rap-program', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'RAP Program',
  } as const),
  util.Page('releases', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Releases',
  } as const),
  util.Page('release', {
    activeMatcher: null,
    featureFlag: null,
    integrations: [PageIntegration.UserFlow],
    layoutVersion: PageLayoutVersion.Version2,
    roles: null,
    title: 'Release',
  } as const),
]);
